import { useEffect, useId, useRef, useState, type FormEvent } from "react";
import { useAuth } from "@/auth/AuthProvider";
import { buttonClass, TOUCH_TARGET } from "@/components/buttonStyles";
import { FIELD_ERROR, FIELD_HELP, FIELD_LABEL } from "@/components/fieldStyles";
import { getDocumentTypes } from "@/lib/brokerDocumentTypes";
import { messageFor } from "@/lib/errors";
import {
  REPLY_MAX,
  replyError,
  replyRefusalOf,
  replyToAction,
  type ActionItem,
  type ReplyRefusal,
} from "@/lib/submissions/actionItems";
import { uploadSubmissionDocument } from "@/lib/submissions/lossRuns";

/** What a reply's files are filed as on the submission, where PMS lets a
 *  broker upload one (`GET /api/broker/document-types?for=submission`). */
const REPLY_DOCUMENT_TYPE = "supporting_document";

type Attached =
  | { key: string; filename: string; kind: "uploading" }
  | { key: string; filename: string; kind: "failed"; message: string }
  | { key: string; filename: string; kind: "attached"; documentId: string };

/**
 * The broker's answer to Signal's request for information (BRK-S464,
 * PMS-S872), under "What Signal needs". Each file goes onto the submission
 * first (POST …/documents, filed as a supporting document) and the reply
 * names it by id; files are offered only where PMS lists that type as one a
 * broker may upload to a submission, so a reply is text alone until it does.
 * Nothing reads as sent until PMS answers with the item.
 *
 * PMS takes no Idempotency-Key here, so a second click while one is on its
 * way is ignored, and a reply with no answer reads the actions again before
 * the broker sends it twice: if it landed, the request has its reply and the
 * form goes; if not, the text is still here.
 */
export function ActionReplyForm({
  submissionId,
  item,
  onChanged,
}: {
  submissionId: string;
  item: ActionItem;
  /** Reads the actions again: once PMS has the reply, or to check. */
  onChanged: () => void | Promise<void>;
}) {
  const { authedRequest, authedUploadWithProgress } = useAuth();
  const fieldId = useId();
  const filesId = useId();
  const errorId = useId();
  const [body, setBody] = useState("");
  const [files, setFiles] = useState<Attached[]>([]);
  const [problem, setProblem] = useState<ReplyRefusal | null>(null);
  const [sending, setSending] = useState(false);
  // PMS has it: the actions, read again, take the request off.
  const [sent, setSent] = useState(false);
  // The guard a second click meets before React re-renders.
  const inFlight = useRef(false);
  const uploading = files.some((f) => f.kind === "uploading");
  // Whether PMS takes a reply's files: a failed read offers none.
  const [attachable, setAttachable] = useState(false);
  useEffect(() => {
    let cancelled = false;
    getDocumentTypes(authedRequest, "submission").then(
      (catalog) => {
        if (!cancelled)
          setAttachable(
            catalog.types.some((t) => t.key === REPLY_DOCUMENT_TYPE),
          );
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, [authedRequest]);

  async function attach(chosen: FileList | null) {
    for (const file of Array.from(chosen ?? [])) {
      const key = `${file.name}-${crypto.randomUUID()}`;
      setFiles((all) => [
        ...all,
        { key, filename: file.name, kind: "uploading" },
      ]);
      try {
        const document = await uploadSubmissionDocument(
          authedUploadWithProgress,
          submissionId,
          { file, type: REPLY_DOCUMENT_TYPE },
        );
        setFiles((all) =>
          all.map((f) =>
            f.key === key
              ? {
                  key,
                  filename: document.filename,
                  kind: "attached",
                  documentId: document.documentId,
                }
              : f,
          ),
        );
      } catch (err) {
        setFiles((all) =>
          all.map((f) =>
            f.key === key
              ? {
                  key,
                  filename: file.name,
                  kind: "failed",
                  message: messageFor(err),
                }
              : f,
          ),
        );
      }
    }
  }

  async function send(event: FormEvent) {
    event.preventDefault();
    if (inFlight.current || uploading) return;
    const refused = replyError(body);
    if (refused) {
      setProblem({ kind: "refused", message: refused });
      return;
    }
    inFlight.current = true;
    setSending(true);
    setProblem(null);
    try {
      await replyToAction(authedRequest, submissionId, item.id, {
        body,
        documentIds: files.flatMap((f) =>
          f.kind === "attached" ? [f.documentId] : [],
        ),
      });
      setSent(true);
      void onChanged();
    } catch (err) {
      const refusal = replyRefusalOf(err);
      setProblem(refusal);
      if (refusal.kind !== "refused") void onChanged();
    } finally {
      inFlight.current = false;
      setSending(false);
    }
  }

  if (sent)
    return (
      <p role="status" className="text-brk-text">
        Your reply is with Signal.
      </p>
    );
  return (
    <form noValidate onSubmit={send} className="flex flex-col gap-2">
      <label htmlFor={fieldId} className={FIELD_LABEL}>
        Your reply
      </label>
      <textarea
        id={fieldId}
        rows={3}
        value={body}
        maxLength={REPLY_MAX}
        onChange={(e) => setBody(e.target.value)}
        aria-invalid={problem ? true : undefined}
        aria-describedby={problem ? errorId : undefined}
        className="w-full rounded-md border border-brk-border bg-brk-panel px-3 py-2 text-brk-text outline-none focus:border-brk-accent focus:ring-2 focus:ring-brk-accent/30"
      />
      {attachable && (
        <div className="flex flex-col gap-1">
          <label htmlFor={filesId} className={FIELD_LABEL}>
            Attach files (optional)
          </label>
          <input
            id={filesId}
            type="file"
            multiple
            disabled={sending}
            onChange={(e) => {
              void attach(e.target.files);
              e.target.value = "";
            }}
            className="text-sm text-brk-text"
          />
          <p className={FIELD_HELP}>
            Each file goes onto the submission, and Signal sees it with your
            reply.
          </p>
        </div>
      )}
      {files.length > 0 && (
        <ul aria-label="Files with this reply" className="flex flex-col gap-1">
          {files.map((f) => (
            <li
              key={f.key}
              className="flex flex-wrap items-center justify-between gap-2 text-sm"
            >
              <span className="min-w-0 break-words text-brk-text">
                {f.filename}
                {f.kind === "uploading" && (
                  <span className="text-brk-dim"> · Uploading…</span>
                )}
                {f.kind === "failed" && (
                  <span className="text-brk-danger">
                    {" "}
                    · Not attached: {f.message}
                  </span>
                )}
              </span>
              {f.kind !== "uploading" && (
                <button
                  type="button"
                  onClick={() =>
                    setFiles((all) => all.filter((x) => x.key !== f.key))
                  }
                  disabled={sending}
                  aria-label={`Remove ${f.filename}`}
                  className={`text-brk-accent hover:underline ${TOUCH_TARGET}`}
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {problem && (
        <p id={errorId} role="alert" className={FIELD_ERROR}>
          {problem.message}
        </p>
      )}
      <button
        type="submit"
        aria-busy={sending || undefined}
        disabled={uploading}
        className={`${buttonClass()} self-start`}
      >
        {sending ? "Sending…" : "Send to Signal"}
      </button>
    </form>
  );
}
